import type {
  AddAdminNoticeRequest,
  AddAdminUserRequest,
  AddAdminUserResponse,
  AddMarketingProductRequest,
  AddMarketingProductResponse,
  AdminNoticeResponse,
  AdminUserRole,
  GetAdminNoticeAudiencesResponse,
  GetAdminNoticeResponse,
  GetAdminNoticesMainParams,
  GetAdminNoticesMainResponse,
  GetAdminPermissionsResponse,
  GetAdminProductOrderResponse,
  GetAdminProductOrdersParams,
  GetAdminProductOrdersResponse,
  GetAdminProductResponse,
  GetAdminProductsParams,
  GetAdminProductsResponse,
  GetAdminUploadUrlsParams,
  GetAdminUploadUrlsResponse,
  GetAdminUserResponse,
  GetAdminUserRolesResponse,
  GetAdminUsersParams,
  GetAdminUsersResponse,
  GetAllBranchesResponse,
  GetOrderNoteLogsParams,
  GetOrderNoteLogsResponse,
  GetOrderStatusLogsParams,
  GetOrderStatusLogsResponse,
  OrderingMarketingProductRequest,
  UpdateAdminNoticeRequest,
  UpdateAdminUserRolePermissionsRequest,
  UpdateInvoiceParams,
  UpdateInvoiceResponse,
  UpdateMarketingProductRequest,
  UpdateMarketingProductResponse,
  UpdateOrderNoteParams,
  UpdateOrderNoteResponse,
  UpdateOrderStatusParams,
  UpdateOrderStatusResponse,
} from '@/types/admin';
import type { PathVariableRequestParams } from '@/types/api';
import axios from '@/utils/axios';

export const getAllBranches = () => {
  return axios
    .get<GetAllBranchesResponse>('/admin/v1/branches/all')
    .then((res) => {
      const result = [...res.data];

      result.sort((a, b) => a.branchName.localeCompare(b.branchName));

      return result;
    });
};

export const getAdminProducts = ({
  page,
  size,
  isPublished,
}: GetAdminProductsParams): Promise<GetAdminProductsResponse> => {
  return axios
    .get(`/admin/v1/products`, {
      params: {
        page,
        size,
        isPublished,
      },
    })
    .then((res) => res.data);
};

export const getAdminProduct = ({
  id,
}: PathVariableRequestParams): Promise<GetAdminProductResponse> => {
  return axios.get(`/admin/v1/products/${id}`).then((res) => res.data);
};

export const orderingMarketingProducts = ({
  page,
  size,
  ids,
}: OrderingMarketingProductRequest): Promise<null> => {
  return axios
    .patch(
      '/admin/v1/products/ordering',
      {
        ids,
      },
      {
        params: {
          page,
          size,
        },
      }
    )
    .then((res) => res.data);
};

export const getAdminProductOrders = ({
  page,
  size,
  branchId,
  userName,
  status,
  isInvoiced,
}: GetAdminProductOrdersParams): Promise<GetAdminProductOrdersResponse> => {
  return axios
    .get('/admin/v1/orders', {
      params: {
        page,
        size,
        branchId,
        userName,
        status,
        isInvoiced,
      },
    })
    .then((res) => res.data);
};

export const getAdminProductOrder = ({
  id,
}: PathVariableRequestParams): Promise<GetAdminProductOrderResponse> => {
  return axios.get(`/admin/v1/orders/${id}`).then((res) => res.data);
};

export const getUploadUrls = ({
  imageCounts = 1,
}: GetAdminUploadUrlsParams): Promise<GetAdminUploadUrlsResponse> => {
  return axios
    .post('/admin/v1/products/upload-urls', {
      imageCounts,
    })
    .then((res) => res.data);
};

export const addMarketingProduct = ({
  name,
  summary,
  isPublished,
  price,
  cost,
  discountPrice,
  bannerImageUrl,
  description,
  additionalDescription,
  note,
}: AddMarketingProductRequest): Promise<AddMarketingProductResponse> => {
  return axios
    .post('/admin/v1/products', {
      name,
      summary,
      isPublished,
      price,
      cost,
      discountPrice,
      bannerImageUrl,
      description,
      additionalDescription,
      note,
    })
    .then((res) => res.data);
};

export const updateMarketingProduct = ({
  id,
  summary,
  price,
  discountPrice,
  cost,
  bannerImageUrl,
  description,
  note,
  additionalDescription,
  isPublished,
}: UpdateMarketingProductRequest): Promise<UpdateMarketingProductResponse> => {
  return axios
    .patch(`/admin/v1/products/${id}`, {
      summary,
      price,
      discountPrice,
      cost,
      bannerImageUrl,
      description,
      note,
      additionalDescription,
      isPublished,
    })
    .then((res) => res.data);
};

export const updateInvoice = ({
  id,
  isInvoiced,
}: UpdateInvoiceParams): Promise<UpdateInvoiceResponse> => {
  return axios.patch(`/admin/v1/orders/${id}/invoices/tax`, { isInvoiced });
};

export const updateOrderStatus = ({
  id,
  status,
}: UpdateOrderStatusParams): Promise<UpdateOrderStatusResponse> => {
  return axios.patch(`/admin/v1/orders/${id}/status`, { status });
};

export const getOrderStatusLogs = ({
  id,
  page,
  size,
}: GetOrderStatusLogsParams): Promise<GetOrderStatusLogsResponse> => {
  return axios
    .get(`/admin/v1/orders/${id}/status-logs`, {
      params: {
        page,
        size,
      },
    })
    .then((res) => res.data);
};

export const updateOrderNote = ({
  id,
  note,
}: UpdateOrderNoteParams): Promise<UpdateOrderNoteResponse> => {
  return axios
    .patch(`/admin/v1/orders/${id}/note`, {
      note,
    })
    .then((res) => res.data);
};

export const getOrderNoteLogs = ({
  id,
  page,
  size,
}: GetOrderNoteLogsParams): Promise<GetOrderNoteLogsResponse> => {
  return axios
    .get(`/admin/v1/orders/${id}/note-logs`, {
      params: {
        page,
        size,
      },
    })
    .then((res) => res.data);
};

/** Notice */
export const getAdminNoticesMain = ({
  page,
  size,
}: GetAdminNoticesMainParams): Promise<GetAdminNoticesMainResponse> => {
  return axios
    .get('/admin/v1/notices/main', {
      params: {
        page,
        size,
      },
    })
    .then((res) => res.data);
};

export const getAdminNotice = ({
  id,
}: PathVariableRequestParams): Promise<GetAdminNoticeResponse> => {
  return axios.get(`/admin/v1/notices/${id}`).then((res) => res.data);
};

export const addAdminNotice = (
  data: AddAdminNoticeRequest
): Promise<AdminNoticeResponse> => {
  return axios.post('/admin/v1/notices', data).then((res) => res.data);
};

export const updateAdminNotice = ({
  id,
  ...data
}: UpdateAdminNoticeRequest): Promise<AdminNoticeResponse> => {
  return axios.patch(`/admin/v1/notices/${id}`, data).then((res) => res.data);
};

export const deleteAdminNotice = ({
  id,
}: PathVariableRequestParams): Promise<void> => {
  return axios.delete(`/admin/v1/notices/${id}`).then((res) => res.data);
};

export const getAdminNoticeAudiences = ({
  id,
}: PathVariableRequestParams): Promise<GetAdminNoticeAudiencesResponse> => {
  return axios.get(`/admin/v1/notices/${id}/audiences`).then((res) => res.data);
};

/** User */
export const getAdminUsers = ({
  page,
  size,
  keyword,
  userType,
  userStatus,
}: GetAdminUsersParams): Promise<GetAdminUsersResponse> => {
  return axios
    .get('/admin/v1/users', {
      params: {
        page,
        size,
        keyword,
        userType,
        userStatus,
      },
    })
    .then((res) => res.data);
};

export const getAdminUser = ({
  id,
}: PathVariableRequestParams): Promise<GetAdminUserResponse> => {
  return axios.get(`/admin/v1/users/${id}`).then((res) => res.data);
};

export const addAdminUser = (
  data: AddAdminUserRequest
): Promise<AddAdminUserResponse> => {
  return axios.post('/admin/v1/users', data).then((res) => res.data);
};

export const getAdminUserRoles = (): Promise<GetAdminUserRolesResponse> => {
  // 역할은 수가 적어 한 번에 받는다
  return axios
    .get('/admin/v1/users/roles', { params: { page: 1, size: 100 } })
    .then((res) => res.data);
};

export const getAdminPermissions = (): Promise<GetAdminPermissionsResponse> => {
  return axios.get('/admin/v1/users/permissions').then((res) => res.data);
};

export const updateAdminUserRolePermissions = ({
  id,
  permissionIds,
}: UpdateAdminUserRolePermissionsRequest): Promise<AdminUserRole> => {
  return axios
    .patch(`/admin/v1/users/roles/${id}`, { permissionIds })
    .then((res) => res.data);
};
